import { useEffect, useState } from 'react'
import MagnumSays from '@/components/MagnumSays'
import { CatalogSection, formatClock, formatDuration } from '@/lib/actModes'
import { cn } from '@/lib/utils'

interface ActFullSectionRunProps {
  section: CatalogSection
  /** Seconds each question gets at today's rung of the ladder. */
  questionBudget: number
  /** Seconds the whole section gets at today's rung. */
  runBudget: number
  answered: number
  correct: number
  questionStartedAt: number | null
  runStartedAt: number | null
  busy: boolean
  onGrade: (correct: boolean) => void
  onEnd: () => void
}

/**
 * The whole section against a clock. No calling anything, no notes -- the only
 * question is whether he gets through all of them in the time the test gives
 * him, so the screen is a clock, a counter, and two buttons.
 */
export default function ActFullSectionRun({
  section,
  questionBudget,
  runBudget,
  answered,
  correct,
  questionStartedAt,
  runStartedAt,
  busy,
  onGrade,
  onEnd,
}: ActFullSectionRunProps) {
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 250)
    return () => clearInterval(id)
  }, [])

  const runElapsed = runStartedAt != null ? Math.floor((now - runStartedAt) / 1000) : 0
  const remaining = Math.max(0, runBudget - runElapsed)
  const questionElapsed = questionStartedAt != null ? Math.floor((now - questionStartedAt) / 1000) : 0
  const outOfTime = remaining === 0

  // Where he should be by now if the budget were being spent evenly.
  const expected = Math.min(section.questions, Math.floor(runElapsed / Math.max(1, questionBudget)))
  const drift = answered - expected
  const paceLabel = drift >= 1 ? `${drift} ahead` : drift <= -1 ? `${Math.abs(drift)} behind` : 'on pace'

  const questionPercent = Math.min(100, (questionElapsed / Math.max(1, questionBudget)) * 100)
  const overBudget = questionElapsed > questionBudget

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between text-sm">
        <span className="font-medium">
          {section.emoji} {section.label}
        </span>
        <button
          type="button"
          onClick={onEnd}
          className="text-muted-foreground hover:text-foreground transition min-h-[36px]"
        >
          End the section
        </button>
      </div>

      <div className="text-center">
        <p
          className={cn(
            'text-6xl font-mono font-bold tabular-nums tracking-tight',
            remaining <= 60 ? 'text-red-600' : remaining <= 300 ? 'text-amber-600' : ''
          )}
          aria-live="off"
        >
          {formatClock(remaining)}
        </p>
        <p className="text-xs text-muted-foreground mt-1">left in the section</p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-2xl font-bold tabular-nums">
            {Math.min(answered + 1, section.questions)}
            <span className="text-base font-normal text-muted-foreground"> / {section.questions}</span>
          </p>
          <p className="text-xs text-muted-foreground">question</p>
        </div>
        <div className="text-center">
          <p
            className={cn(
              'text-2xl font-bold tabular-nums',
              drift <= -2 ? 'text-red-600' : drift >= 1 ? 'text-green-600' : ''
            )}
          >
            {paceLabel}
          </p>
          <p className="text-xs text-muted-foreground">{formatDuration(questionBudget)} a question</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold tabular-nums">{correct}</p>
          <p className="text-xs text-muted-foreground">right so far</p>
        </div>
      </div>

      <div>
        <div className="h-2 rounded-full bg-accent overflow-hidden">
          <div
            className={cn('h-full transition-all', overBudget ? 'bg-red-500' : 'bg-primary')}
            style={{ width: `${questionPercent}%` }}
          />
        </div>
        <p className="text-xs text-muted-foreground mt-1 text-center tabular-nums">
          {formatClock(questionElapsed)} on this one
          {overBudget && <span className="text-red-600 font-medium"> · over budget, move</span>}
        </p>
      </div>

      {outOfTime ? (
        <div className="space-y-4">
          <MagnumSays variant="warning">
            Time. On the real thing they'd be taking the booklet out of your hands right now — you got
            through {answered} of {section.questions}. Let's see the damage.
          </MagnumSays>
          <button
            type="button"
            onClick={onEnd}
            className="w-full px-6 py-4 bg-primary text-primary-foreground rounded-xl font-bold hover:opacity-90 transition min-h-[44px]"
          >
            See how it went
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => onGrade(true)}
            disabled={busy}
            className="px-4 py-6 rounded-xl border-2 border-green-500 bg-green-50 text-green-800 font-bold text-lg hover:bg-green-100 transition disabled:opacity-40 min-h-[44px]"
          >
            Right
          </button>
          <button
            type="button"
            onClick={() => onGrade(false)}
            disabled={busy}
            className="px-4 py-6 rounded-xl border-2 border-red-400 bg-red-50 text-red-800 font-bold text-lg hover:bg-red-100 transition disabled:opacity-40 min-h-[44px]"
          >
            Wrong
          </button>
        </div>
      )}
    </div>
  )
}
